import { Badge, Icon, LoadingSpinner, Popover, Text, BadgeProps } from "@oysterhr/ui"

import { type PropsWithChildren, useCallback, useState } from "react"
import { BadgeDropdownItem } from "./types"
import classNames from "classnames"

export interface BadgeDropdownProps extends Pick<BadgeProps, "variation" | "size" | "isOutline"> {
  values?: BadgeDropdownItem[]
  loading?: boolean
}

export const BadgeDropdown = ({
  values,
  loading = false,
  children,
  ...rest
}: PropsWithChildren<BadgeDropdownProps>) => {
  const [isOpen, setIsOpen] = useState(false)
  const toggle = useCallback(() => !loading && setIsOpen(isOpen => !isOpen), [loading])

  if (!values?.length) {
    return (
      <span className="flex">
        <Badge {...rest} className="flex items-center whitespace-nowrap">
          {children}
        </Badge>
      </span>
    )
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} placement="bottom-start">
      <Popover.Trigger>
        <button
          className="flex rounded-full"
          onClick={e => {
            e.stopPropagation()
            toggle()
          }}
        >
          <Badge {...rest} className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap">
            {loading ? (
              <LoadingSpinner />
            ) : (
              <>
                {children} <Icon name="ChevronDown" data-testid="badge-dropdown-icon" />
              </>
            )}
          </Badge>
        </button>
      </Popover.Trigger>
      <Popover.Content>
        <span className="flex flex-col gap-1" data-testid="badge-dropdown-content">
          {values.map((value, index) => (
            <button
              data-testid={
                typeof value.title === "string" ? value.title : `badge-dropdown-item-${index}`
              }
              className={classNames("whitespace-nowrap rounded px-4 py-2 text-left", {
                "transition-all hover:bg-gray-200": !value.disabled,
                "cursor-not-allowed text-gray-300": value.disabled,
              })}
              key={typeof value.title === "string" ? value.title : index}
              disabled={value.disabled}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                value.onClick()
                setIsOpen(false)
              }}
            >
              <Text size="small">{value.title}</Text>
            </button>
          ))}
        </span>
      </Popover.Content>
    </Popover>
  )
}
